import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, BellRing } from 'lucide-react';

import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput';
import { formatDateTime } from '../../../utils/Utils';

// P1-C — tebliğ/okudum banner'ı: sonuçlar görünür olduğunda "Sonuçlarınız
// tebliğ edilmiştir" + "Okudum" onayı (opsiyonel karşı-not). Onay idempotent —
// BE mevcut kaydı aynen döner; onaylanmış durumda tarih + (varsa) not gösterilir.
const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${({ $acknowledged }) => ($acknowledged ? '#DCFCE7' : '#EFF6FF')};
  border: 1px solid ${({ $acknowledged }) => ($acknowledged ? '#BBF7D0' : '#DBEAFE')};
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #fff;
  color: ${({ $acknowledged, theme }) => ($acknowledged ? '#16A34A' : theme.colors.primary.main)};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const BannerText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 10px;
  white-space: pre-wrap;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  > :first-child {
    flex: 1;
    min-width: 220px;
  }
`;

export default function AcknowledgementBanner({ acknowledgement, onAcknowledge }) {
  const { t, i18n } = useTranslation();
  const [counterNote, setCounterNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAcknowledge = useCallback(async () => {
    setSaving(true);
    await onAcknowledge(counterNote.trim() || null);
    setSaving(false);
  }, [onAcknowledge, counterNote]);

  if (acknowledgement) {
    return (
      <Banner $acknowledged>
        <IconWrap $acknowledged><CheckCircle2 size={20} /></IconWrap>
        <Body>
          <BannerTitle>{t('performance.results.acknowledgedTitle')}</BannerTitle>
          <BannerText>
            {t('performance.results.acknowledgedAt')}: {formatDateTime(acknowledgement.acknowledgedAt, i18n.language)}
            {acknowledgement.counterNote ? `\n${t('performance.results.counterNote')}: ${acknowledgement.counterNote}` : ''}
          </BannerText>
        </Body>
      </Banner>
    );
  }

  return (
    <Banner>
      <IconWrap><BellRing size={20} /></IconWrap>
      <Body>
        <BannerTitle>{t('performance.results.announcedTitle')}</BannerTitle>
        <BannerText>{t('performance.results.announcedText')}</BannerText>
        <ActionsRow>
          <TextInput
            label={t('performance.results.counterNoteOptional')}
            value={counterNote}
            onChange={(e) => setCounterNote(e.target.value)}
          />
          <Button onClick={handleAcknowledge} disabled={saving}>
            {saving ? t('common.saving') : t('performance.results.acknowledge')}
          </Button>
        </ActionsRow>
      </Body>
    </Banner>
  );
}
